import { activeLocale } from '@/utils/locale';
/**
 * Shared time-label formatting for version/history rows.
 *
 * The display contract (used by the artifact versions dropdown; adopt for any
 * new timestamp rows instead of hand-rolling another "Xm ago" helper):
 *   - < 7 days old   → localized relative time ("5 minutes ago" / "לפני שעה"),
 *                      via Intl.RelativeTimeFormat — no i18n keys needed.
 *   - ≥ 7 days, same year → absolute "Jul 9, 03:16 PM" (relative would be vague).
 *   - different year → "Jun 17, 2025" (the year is the essential fact).
 *   - tooltips       → formatExactTime() — full year + date + time, always.
 */

/** Compact absolute stamp: "Jun 21, 14:30" this year, "Jun 21, 2025" otherwise. */
export function formatCompactTime(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  if (d.getFullYear() !== new Date().getFullYear()) {
    return d.toLocaleString(activeLocale(), { year: 'numeric', month: 'short', day: 'numeric' });
  }
  return d.toLocaleString(activeLocale(), {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/** Full-precision timestamp (year + date + time, always) — for hover tooltips. */
export function formatExactTime(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString(activeLocale(), {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// One formatter instance per language — constructing RelativeTimeFormat does a
// locale-data lookup, and callers may format on every render of a hot path.
let rtfCache: { lang: string; rtf: Intl.RelativeTimeFormat } | null = null;
function getRtf(): Intl.RelativeTimeFormat {
  const lang = activeLocale();
  if (!rtfCache || rtfCache.lang !== lang) {
    rtfCache = { lang, rtf: new Intl.RelativeTimeFormat(lang, { numeric: 'auto' }) };
  }
  return rtfCache.rtf;
}

/** Localized relative time, hard-capped at 7 days (see contract above). */
export function formatRelativeTime(iso?: string | number | Date): string {
  if (!iso) return '';
  const d = iso instanceof Date ? iso : new Date(iso);
  if (isNaN(d.getTime())) return '';
  const diffSec = Math.round((d.getTime() - Date.now()) / 1000);
  const abs = Math.abs(diffSec);
  if (abs >= 7 * 86400) return formatCompactTime(d.toISOString());
  try {
    const rtf = getRtf();
    if (abs < 60) return rtf.format(diffSec, 'second');
    if (abs < 3600) return rtf.format(Math.trunc(diffSec / 60), 'minute');
    if (abs < 86400) return rtf.format(Math.trunc(diffSec / 3600), 'hour');
    return rtf.format(Math.trunc(diffSec / 86400), 'day');
  } catch {
    return formatCompactTime(d.toISOString());
  }
}
